import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Divider,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import { 
  Google as GoogleIcon,
  TrendingUp as StocksIcon,
  Newspaper as NewsIcon,
  FitnessCenter as GymIcon,
  Restaurant as FoodIcon,
  StickyNote2 as NotesIcon,
  Bedtime as SleepIcon,
  AutoStories as BooksIcon,
  Dashboard as TasksIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  Laptop as LaptopIcon,
  Smartphone as PhoneIcon,
  HourglassEmpty as SandClockIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import Footer from './Footer';

const LoginScreen = () => {
  const { signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate('/news');
    } catch (error) {
      console.error('Error signing in:', error);
    }
  };

  const trackerFeatures = [
    {
      icon: <StocksIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'Stocks Watcher',
      description: 'Real-time market monitoring',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    },
    {
      icon: <NewsIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'News Tracker',
      description: 'Stay informed with latest news',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    },
    {
      icon: <GymIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'Fitness Tracker',
      description: 'Track workouts and progress',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    },
    {
      icon: <FoodIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'Food Tracker',
      description: 'Monitor nutrition and meals',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    },
    {
      icon: <NotesIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'Notes Tracker',
      description: 'Organize thoughts and ideas',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    },
    {
      icon: <SleepIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'Sleep Tracker',
      description: 'Optimize your rest patterns',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    },
    {
      icon: <BooksIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'Books Tracker',
      description: 'Manage your reading list',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    },
    {
      icon: <TasksIcon sx={{ fontSize: 32, color: '#0D47A1' }} />,
      title: 'Task Manager',
      description: 'AI-powered productivity',
      color: '#0D47A1',
      bgColor: '#f0f4ff'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1498050108023-c5249f4df085?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: 'white',
          py: 8,
          textAlign: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13, 71, 161, 0.8) 0%, rgba(21, 101, 192, 0.8) 100%)',
            zIndex: 1,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Logo size="large" showTagline={false} />
              <Typography
                variant="display1"
                sx={{
                  color: 'white',
                  margin: 0,
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontSize: '3.5rem !important' // Force the reduced size
                }}
              >
                Life Bricks
              </Typography>
            </Box>
          </Box>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: 'auto', fontSize: '0.875rem !important' }}>
            Your personal command center for life optimization. Real-time tracking, smart insights, and seamless organization across all aspects of your daily routine.
          </Typography>
          
          {/* Productivity Stats */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 6, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: '1.4rem !important' }}>12+</Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>Trackers</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: '1.4rem !important' }}>AI-Powered</Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>Insights</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: '1.4rem !important' }}>Real-time</Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>Updates</Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            sx={{
              py: 1,
              px: 2,
              fontSize: '0.8rem',
              fontWeight: 600,
              backgroundColor: '#1f2937',
              color: 'white',
              borderRadius: 1,
              minWidth: 140,
              '&:hover': {
                backgroundColor: '#f5f5f5',
                color: '#1f2937',
                boxShadow: 'none',
              },
            }}
          >
            Login with Google
          </Button>
        </Container>
      </Box>

      {/* Tracker Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" sx={{ fontWeight: 700, mb: 2, color: '#0D47A1' }}>
          Everything You Need to Stay Productive
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
          Comprehensive tracking tools designed for modern professionals who want to optimize every aspect of their lives.
        </Typography>

        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {trackerFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: 'flex' }}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 1,
                  backgroundColor: feature.bgColor,
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: 200,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(13, 71, 161, 0.15)',
                    borderColor: '#0D47A1',
                  },
                }}
              >
                <Box>
                  <Box sx={{ mb: 2, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: feature.color, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem !important' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>
                    {feature.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Tech Professional Section with Background Images */}
      <Box 
        sx={{ 
          py: 8,
          position: 'relative',
          backgroundImage: 'url(https://cdn.pixabay.com/photo/2016/11/29/08/41/apple-1868496_1280.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(13, 71, 161, 0.8)',
            zIndex: 1,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          {/* Built for Tech Professionals Section */}
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: 'white' }}>
              Built for Tech Professionals
            </Typography>
            <Typography variant="h6" color="rgba(255, 255, 255, 0.9)" sx={{ mb: 4, lineHeight: 1.7, maxWidth: 800, mx: 'auto' }}>
              Designed specifically for developers, designers, and tech professionals who need to stay organized and productive. 
              Build your life brick by brick - track your stocks, monitor market news, manage your fitness goals, and organize your tasks all in one place.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4, justifyContent: 'center' }}>
              <Chip 
                label="Real-time Data" 
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white', 
                  borderColor: 'rgba(255, 255, 255, 0.3)' 
                }} 
                variant="outlined" 
              />
              <Chip 
                label="AI Analytics" 
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white', 
                  borderColor: 'rgba(255, 255, 255, 0.3)' 
                }} 
                variant="outlined" 
              />
              <Chip 
                label="Cross-platform" 
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white', 
                  borderColor: 'rgba(255, 255, 255, 0.3)' 
                }} 
                variant="outlined" 
              />
              <Chip 
                label="Secure" 
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                  color: 'white', 
                  borderColor: 'rgba(255, 255, 255, 0.3)' 
                }} 
                variant="outlined" 
              />
            </Box>
          </Box>

        </Container>
      </Box>

      {/* Footer CTA */}
      <Box sx={{ backgroundColor: '#0D47A1', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to Optimize Your Life?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Start building your organized and productive life with our comprehensive life management platform.
          </Typography>
            <Button
              variant="contained"
              size="medium"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              sx={{
                py: 1.5,
                px: 3,
                fontSize: '0.9rem',
                fontWeight: 600,
                backgroundColor: 'white',
                color: '#0D47A1',
                borderRadius: 1.5,
                minWidth: 160,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  color: '#0D47A1',
                  boxShadow: 'none',
                },
              }}
            >
              Login with Google
            </Button>
            <Typography
              variant="caption"
            sx={{ mt: 3, display: 'block', opacity: 0.8 }}
            >
              By signing in, you agree to our Terms of Service and Privacy Policy
            </Typography>
        </Container>
      </Box>
      
      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LoginScreen;
